import { Controller } from '@hotwired/stimulus'

export default (class extends Controller {
  static targets = [
    'plot', 'point', 'tooltip', 'month', 'value', 'guide',
    'delta', 'deltaUp', 'deltaDown', 'deltaText'
  ]
  static values = {
    labels: Array, series: Array, format: String, width: Number, padding: Number
  }

  connect() {
    this.index = 0
    this.#update()
    this.hide()
  }

  point(event) {
    const bounds = this.plotTarget.getBoundingClientRect()
    const x = event.clientX - bounds.left
    const position = x / bounds.width * this.widthValue
    const fraction = (position - this.paddingValue) /
      (this.widthValue - 2 * this.paddingValue)
    this.index = Math.max(0, Math.min(this.labelsValue.length - 1,
      Math.round(fraction * (this.labelsValue.length - 1))))
    this.#update()
    this.#position(x, event.clientY - bounds.top)
  }

  navigate(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Escape']
    if (!keys.includes(event.key)) return
    event.preventDefault()
    if (event.key === 'Escape') return this.hide()
    if (event.key === 'Home') this.index = 0
    else if (event.key === 'End') this.index = this.labelsValue.length - 1
    else {
      const next = ['ArrowRight', 'ArrowUp'].includes(event.key) ? 1 : -1
      this.index = Math.max(0,
        Math.min(this.labelsValue.length - 1, this.index + next))
    }
    this.show()
  }

  show() {
    this.#update()
    const bounds = this.plotTarget.getBoundingClientRect()
    this.#position(this.#x() / this.widthValue * bounds.width, 0)
  }

  release(event) {
    if (event.pointerType === 'touch') this.hide()
  }

  hide() {
    this.tooltipTarget.hidden = true
    this.element.dataset.active = 'false'
    this.pointTargets.forEach(point => { point.dataset.active = 'false' })
  }

  #x() {
    return this.paddingValue + this.index / (this.labelsValue.length - 1) *
      (this.widthValue - 2 * this.paddingValue)
  }

  #position(x, y) {
    this.tooltipTarget.hidden = false
    this.element.dataset.active = 'true'
    const bounds = this.plotTarget.getBoundingClientRect()
    const tip = this.tooltipTarget.getBoundingClientRect()
    const left = Math.max(0, Math.min(bounds.width - tip.width, x - tip.width / 2))
    const top = Math.max(8 - bounds.top, y - tip.height - 12)
    this.tooltipTarget.style.left = `${left}px`
    this.tooltipTarget.style.top = `${top}px`
  }

  #update() {
    const values = this.seriesValue.map(series => series.values[this.index])
    this.monthTarget.textContent = this.labelsValue[this.index]
    this.valueTargets.forEach(target => {
      target.textContent = this.#format(values[Number(target.dataset.series)])
    })
    if (this.hasDeltaTarget) {
      const change = (values[1] - values[0]) / values[0] * 100
      const negative = change < 0
      this.deltaTarget.dataset.negative = String(negative)
      this.deltaUpTarget.hidden = negative
      this.deltaDownTarget.hidden = !negative
      this.deltaTextTarget.textContent =
        `${Math.abs(Math.round(change))}% YoY`
    }
    const rows = this.seriesValue.map((series, index) =>
      `${series.label}: ${this.#format(values[index])}`)
    const description = [this.labelsValue[this.index], ...rows].join(', ')
    this.plotTarget.setAttribute('aria-valuenow', this.index)
    this.plotTarget.setAttribute('aria-valuetext', description)
    this.guideTarget.setAttribute('x1', this.#x())
    this.guideTarget.setAttribute('x2', this.#x())
    this.pointTargets.forEach(point => {
      point.dataset.active = String(Number(point.dataset.month) === this.index)
    })
  }

  #format(value) {
    if (this.formatValue === 'percent') return `${value.toFixed(1)}%`
    if (this.formatValue === 'ratio') {
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 1, maximumFractionDigits: 2
      })
    }
    if (this.formatValue === 'compact') return `$${Math.round(value / 1000)}K`
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0
    }).format(value)
  }
})
